import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  X, FileText, Plus, Trash2, User, Bot, Save, Send, Lock, Loader, Eye, Info,
} from 'lucide-react';
import invoiceService from '../../api/services/invoiceService';
import productService from '../../api/services/productService';
import { useToast } from '../../contexts/ToastContext';
import InvoicePreview from './InvoicePreview';
import { DEFAULT_BRANDING } from './branding';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CURRENCIES = ['EUR', 'XAF', 'USD', 'GBP', 'XOF'];

const formatMoney = (amount, currency = 'EUR') => {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};

// ISO / Date -> 'YYYY-MM-DD' pour <input type="date">
const toDateInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const randKey = () => Math.random().toString(36).slice(2, 9);

// Normalise une ligne d'item venant du backend ou créée à la main
const normalizeItem = (item = {}) => ({
  _key: randKey(),
  productId: item.productId || item.product || '',
  description: item.description || item.productName || item.name || '',
  quantity: item.quantity != null ? item.quantity : 1,
  unitPrice: item.unitPrice != null ? item.unitPrice : (item.price != null ? item.price : 0),
});

const emptyItem = () => ({ _key: randKey(), productId: '', description: '', quantity: 1, unitPrice: 0 });

const isAgentCreated = (inv) =>
  inv?.createdBy === 'agent' ||
  inv?.source === 'agent' ||
  inv?.metadata?.source === 'whatsapp_ai';

// ─── Composant ────────────────────────────────────────────────────────────────

/**
 * Modale de création / édition manuelle d'une facture.
 * - `invoice = null` → création de zéro.
 * - `invoice` avec status 'draft' → édition complète (y compris brouillons IA).
 * - `invoice` avec status !== 'draft' → lecture seule.
 *
 * Le DESIGN (logo, modèle, couleur, société) n'est PAS éditable ici : il provient
 * du modèle GLOBAL de l'entreprise (`branding`), réglé via « Modèle de facture ».
 * On l'utilise seulement pour l'aperçu.
 */
export default function FactureModal({ invoice, onClose, onSaved, branding }) {
  const toast = useToast();
  const isEdit = !!invoice?._id;
  const readOnly = isEdit && invoice.status !== 'draft';

  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('form');

  // ── État du formulaire ──────────────────────────────────────────────────────
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [taxRate, setTaxRate] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  // Pré-remplissage en mode édition / brouillon IA
  useEffect(() => {
    if (!invoice) return;
    setClientName(invoice.clientName || invoice.client?.name || '');
    setClientPhone(invoice.clientPhone || invoice.client?.phone || '');
    setClientEmail(invoice.clientEmail || invoice.client?.email || '');
    const rawItems = Array.isArray(invoice.items) ? invoice.items : [];
    setItems(rawItems.length ? rawItems.map(normalizeItem) : [emptyItem()]);
    // Le backend stocke taxRate en fraction (0.20) ; l'état le garde en % (20) pour la saisie.
    setTaxRate(invoice.taxRate != null ? invoice.taxRate * 100 : 0);
    setCurrency(invoice.currency || 'EUR');
    setDueDate(toDateInput(invoice.dueDate));
    setNotes(invoice.notes || '');
  }, [invoice]);

  // Chargement des produits pour la sélection des lignes
  useEffect(() => {
    let active = true;
    productService
      .getProducts({ limit: 1000 })
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setProducts(list);
      })
      .catch((err) => console.warn('⚠️ Erreur chargement produits (factures):', err));
    return () => { active = false; };
  }, []);

  // ── Calculs live ──────────────────────────────────────────────────────────────
  const { subtotal, taxAmount, total } = useMemo(() => {
    const sub = items.reduce(
      (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0),
      0
    );
    const tax = sub * ((Number(taxRate) || 0) / 100);
    return { subtotal: sub, taxAmount: tax, total: sub + tax };
  }, [items, taxRate]);

  // ── Manipulation des lignes ───────────────────────────────────────────────────
  const updateItem = useCallback((key, patch) => {
    setItems((prev) => prev.map((it) => (it._key === key ? { ...it, ...patch } : it)));
  }, []);

  const handleSelectProduct = useCallback((key, productId) => {
    const product = products.find((p) => p._id === productId);
    if (!product) {
      updateItem(key, { productId: '' });
      return;
    }
    updateItem(key, {
      productId,
      description: product.name || '',
      unitPrice: product.price != null ? product.price : 0,
    });
  }, [products, updateItem]);

  const addItem = useCallback(() => setItems((prev) => [...prev, emptyItem()]), []);
  const removeItem = useCallback((key) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((it) => it._key !== key) : prev));
  }, []);

  // ── Aperçu (utilise le modèle GLOBAL, non éditable ici) ───────────────────────
  const b = useMemo(() => ({ ...DEFAULT_BRANDING, ...(branding || {}) }), [branding]);
  const brandingData = useMemo(() => ({
    logoUrl: b.logoUrl,
    companyName: b.companyName,
    companyAddress: b.companyAddress,
    companyPhone: b.companyPhone,
    companyEmail: b.companyEmail,
    brandColor: b.brandColor,
  }), [b]);

  const previewInvoice = useMemo(() => ({
    invoiceNumber: invoice?.invoiceNumber,
    issueDate: invoice?.issueDate || invoice?.createdAt,
    dueDate,
    clientName,
    clientEmail,
    clientPhone,
    items: items.map((it) => ({
      description: it.description,
      quantity: Number(it.quantity) || 0,
      unitPrice: Number(it.unitPrice) || 0,
    })),
    subtotal,
    taxAmount,
    total,
    taxRate: (Number(taxRate) || 0) / 100,
    discount: Number(invoice?.discount) || 0,
    currency,
    notes,
  }), [invoice, dueDate, clientName, clientEmail, clientPhone, items, subtotal, taxAmount, total, taxRate, currency, notes]);

  // ── Sauvegarde ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (readOnly || saving) return;

    if (!clientName.trim()) {
      toast.error('Le nom du client est requis');
      return;
    }
    if (!clientEmail.trim()) {
      toast.error("L'email du client est requis");
      return;
    }
    const validItems = items.filter(
      (it) => it.description.trim() && Number(it.quantity) > 0
    );
    if (validItems.length === 0) {
      toast.error('Ajoutez au moins une ligne valide (description + quantité)');
      return;
    }

    const payload = {
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim(),
      clientPhone: clientPhone.trim(),
      items: validItems.map((it) => ({
        ...(it.productId ? { productId: it.productId } : {}),
        description: it.description.trim(),
        quantity: Number(it.quantity) || 0,
        unitPrice: Number(it.unitPrice) || 0,
      })),
      // Le backend attend taxRate en FRACTION décimale (0.20 = 20%), pas en %.
      taxRate: (Number(taxRate) || 0) / 100,
      currency,
      ...(dueDate ? { dueDate } : {}),
      notes: notes.trim(),
      // Le design (logo/modèle/couleur) est GLOBAL : pas de branding par facture.
      // subtotal / taxAmount / total et le total par ligne sont recalculés côté backend.
    };

    setSaving(true);
    try {
      const res = isEdit
        ? await invoiceService.updateInvoice(invoice._id, payload)
        : await invoiceService.createInvoice(payload);
      const saved = res?.data || res;
      toast.success(
        isEdit ? 'Brouillon mis à jour' : 'Facture créée',
        { title: isEdit ? 'Facture modifiée' : 'Nouvelle facture' }
      );
      onSaved?.(saved);
    } catch (err) {
      console.error('❌ Erreur sauvegarde facture:', err);
      toast.error(err?.error?.message || err?.message || "Impossible d'enregistrer la facture");
    } finally {
      setSaving(false);
    }
  }, [
    readOnly, saving, clientName, clientPhone, clientEmail, items, taxRate,
    currency, dueDate, notes, isEdit, invoice, onSaved, toast,
  ]);

  const title = isEdit
    ? (readOnly ? `Facture ${invoice.invoiceNumber || ''}` : `Éditer la facture ${invoice.invoiceNumber || ''}`)
    : 'Nouvelle facture';

  const inputCls =
    'w-full text-ink text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green disabled:opacity-60 disabled:cursor-not-allowed';
  const inputStyle = { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="card rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-6 flex-shrink-0" style={{ borderBottom: '0.5px solid var(--c-border)', background: 'var(--c-primary-dark)' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                <FileText style={{ color: 'rgb(var(--c-accent-green-rgb))' }} size={28} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl sm:text-2xl font-semibold text-ink truncate">{title}</h2>
                  {isEdit && isAgentCreated(invoice) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-md text-xs font-semibold">
                      <Bot size={12} /> Brouillon IA
                    </span>
                  )}
                </div>
                <p className="text-label text-sm">
                  {readOnly
                    ? 'Facture verrouillée — lecture seule (statut non modifiable)'
                    : 'Le brouillon IA et la création manuelle passent par le même formulaire'}
                </p>
              </div>
            </div>
            <motion.button
              onClick={onClose}
              className="p-3 hover:bg-[var(--c-hover)] rounded-xl text-label hover:text-ink transition-all flex-shrink-0"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={24} />
            </motion.button>
          </div>
        </div>

        {/* Bandeau lecture seule */}
        {readOnly && (
          <div className="flex items-center gap-2 px-4 sm:px-6 py-2.5 bg-yellow-500/10 border-b border-yellow-500/20 flex-shrink-0">
            <Lock size={14} className="text-yellow-500 flex-shrink-0" />
            <span className="text-xs text-yellow-500">
              Cette facture a déjà été envoyée — les champs ne sont plus modifiables.
            </span>
          </div>
        )}

        {/* Onglets Formulaire / Aperçu */}
        <div className="flex gap-1 px-4 sm:px-6 pt-3 flex-shrink-0" style={{ borderBottom: '0.5px solid var(--c-border)' }}>
          {[
            { id: 'form', label: 'Formulaire', Icon: FileText },
            { id: 'preview', label: 'Aperçu', Icon: Eye },
          ].map((t) => {
            const TabIcon = t.Icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-semibold rounded-t-lg flex items-center gap-1.5 transition-all ${
                  tab === t.id ? 'text-ink' : 'text-label hover:text-ink'
                }`}
                style={tab === t.id ? { background: 'var(--c-primary-dark)', borderBottom: '2px solid rgb(var(--c-accent-green-rgb))' } : {}}
              >
                <TabIcon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Contenu */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-6">
          {tab === 'preview' ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-label">
                <Info size={13} className="text-accent-green" />
                Rendu avec le modèle global de l'entreprise (réglable via « Modèle de facture »).
              </div>
              <div className="bg-[#0d1117] rounded-2xl p-4 sm:p-6 border border-primary-dark/50">
                <InvoicePreview templateId={b.templateId} invoice={previewInvoice} branding={brandingData} />
              </div>
            </div>
          ) : (
          <>
          {/* Client */}
          <div className="rounded-2xl p-4 sm:p-5" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
            <h3 className="text-base font-semibold text-ink mb-4 flex items-center gap-2">
              <User className="text-accent-green" size={18} />
              Client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="text-label text-xs mb-1 block">Nom *</label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  disabled={readOnly}
                  placeholder="Nom du client"
                  className={inputCls} style={inputStyle}
                />
              </div>
              <div>
                <label className="text-label text-xs mb-1 block">Téléphone</label>
                <input
                  type="text"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  disabled={readOnly}
                  placeholder="+237 6XX XXX XXX"
                  className={inputCls} style={inputStyle}
                />
              </div>
              <div>
                <label className="text-label text-xs mb-1 block">Email *</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  disabled={readOnly}
                  placeholder="client@email.com"
                  className={inputCls} style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Lignes d'items */}
          <div className="rounded-2xl p-4 sm:p-5" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-ink flex items-center gap-2">
                <FileText className="text-accent-green" size={18} />
                Lignes
              </h3>
              {!readOnly && (
                <button
                  onClick={addItem}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-green/15 border border-accent-green/30 text-accent-green rounded-lg text-xs font-semibold hover:bg-accent-green/25 transition-all"
                >
                  <Plus size={14} />
                  Ajouter une ligne
                </button>
              )}
            </div>

            <div className="space-y-3">
              {items.map((it) => {
                const lineTotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                return (
                  <div
                    key={it._key}
                    className="grid grid-cols-12 gap-2 items-end rounded-xl p-3"
                    style={{ background: 'var(--c-primary-card)', border: '0.5px solid var(--c-border)' }}
                  >
                    {/* Produit (optionnel) */}
                    <div className="col-span-12 md:col-span-3">
                      <label className="text-label text-[11px] mb-1 block">Produit</label>
                      <select
                        value={it.productId || ''}
                        onChange={(e) => handleSelectProduct(it._key, e.target.value)}
                        disabled={readOnly}
                        className={inputCls + ' cursor-pointer'}
                      >
                        <option value="">— Libre —</option>
                        {products.map((p) => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Description */}
                    <div className="col-span-12 md:col-span-4">
                      <label className="text-label text-[11px] mb-1 block">Description</label>
                      <input
                        type="text"
                        value={it.description}
                        onChange={(e) => updateItem(it._key, { description: e.target.value })}
                        disabled={readOnly}
                        placeholder="Description de la ligne"
                        className={inputCls} style={inputStyle}
                      />
                    </div>

                    {/* Quantité */}
                    <div className="col-span-4 md:col-span-1">
                      <label className="text-label text-[11px] mb-1 block">Qté</label>
                      <input
                        type="number"
                        min="0"
                        value={it.quantity}
                        onChange={(e) => updateItem(it._key, { quantity: e.target.value })}
                        disabled={readOnly}
                        className={inputCls} style={inputStyle}
                      />
                    </div>

                    {/* Prix unitaire */}
                    <div className="col-span-4 md:col-span-2">
                      <label className="text-label text-[11px] mb-1 block">Prix U.</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={it.unitPrice}
                        onChange={(e) => updateItem(it._key, { unitPrice: e.target.value })}
                        disabled={readOnly}
                        className={inputCls} style={inputStyle}
                      />
                    </div>

                    {/* Total ligne + suppression */}
                    <div className="col-span-4 md:col-span-2 flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <label className="text-label text-[11px] mb-1 block">Total</label>
                        <p className="text-ink font-semibold text-sm py-2 truncate">
                          {formatMoney(lineTotal, currency)}
                        </p>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={() => removeItem(it._key)}
                          className="p-2 hover:bg-red-500/20 rounded-lg text-red-500 transition-all flex-shrink-0 disabled:opacity-40"
                          disabled={items.length <= 1}
                          title="Supprimer la ligne"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Paramètres + récap */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Paramètres */}
            <div className="rounded-2xl p-4 sm:p-5 space-y-3" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
              <h3 className="text-base font-semibold text-ink mb-1">Paramètres</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-label text-xs mb-1 block">TVA (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    disabled={readOnly}
                    className={inputCls} style={inputStyle}
                  />
                </div>
                <div>
                  <label className="text-label text-xs mb-1 block">Devise</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    disabled={readOnly}
                    className={inputCls + ' cursor-pointer'}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-label text-xs mb-1 block">Échéance</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={readOnly}
                  className={inputCls + ' cursor-pointer'}
                />
              </div>
              <div>
                <label className="text-label text-xs mb-1 block">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={readOnly}
                  rows={3}
                  placeholder="Notes ou conditions de paiement…"
                  className={inputCls + ' resize-none'}
                />
              </div>
            </div>

            {/* Récap live */}
            <div className="rounded-2xl p-4 sm:p-5" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
              <h3 className="text-base font-semibold text-ink mb-4">Récapitulatif</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between text-label">
                  <span>Sous-total</span>
                  <span>{formatMoney(subtotal, currency)}</span>
                </div>
                <div className="flex justify-between text-label">
                  <span>TVA ({Number(taxRate) || 0}%)</span>
                  <span>{formatMoney(taxAmount, currency)}</span>
                </div>
                <div className="flex justify-between text-ink font-bold text-lg pt-2.5" style={{ borderTop: '0.5px solid var(--c-border)' }}>
                  <span>Total</span>
                  <span className="text-accent-green">{formatMoney(total, currency)}</span>
                </div>
              </div>
            </div>
          </div>
          </>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 sm:p-6 flex items-center justify-end gap-3 flex-shrink-0" style={{ borderTop: '0.5px solid var(--c-border)', background: 'var(--c-primary-dark)' }}>
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-label hover:text-ink rounded-xl font-medium transition-all hover:bg-[var(--c-hover)]"
            style={{ background: 'var(--c-primary-card)', border: '0.5px solid var(--c-border)' }}
          >
            {readOnly ? 'Fermer' : 'Annuler'}
          </button>
          {!readOnly && (
            <motion.button
              onClick={handleSubmit}
              disabled={saving}
              whileHover={{ scale: saving ? 1 : 1.02 }}
              whileTap={{ scale: saving ? 1 : 0.98 }}
              className="px-5 py-2.5 bg-gradient-to-r from-accent-green to-emerald-500 text-black rounded-xl font-bold flex items-center gap-2 hover:shadow-lg hover:shadow-accent-green/25 transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader size={18} className="animate-spin" />
              ) : isEdit ? (
                <Save size={18} />
              ) : (
                <Send size={18} />
              )}
              {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer le brouillon' : 'Créer la facture'}
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
